/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useCallback, useReducer, useEffect } from 'react'
import { set, cloneDeep, get } from 'lodash'
import api, { ILease, IFile, ILeaseFullEvent, ILeaseEvent, IAsset, FormErrors } from '@api'
import { Toaster } from '@ui'
import { LeaseFormErrors } from '../Form'
import { ApolloError } from 'apollo-boost'

enum Actions {
  LOAD,
  UPDATE,
  SET_IS_SAVING,
  SET_LOCAL_FILES,
  SET_ERRORS,
  SET_IS_EDITING,
  SET_IS_DIRTY,
  ADD_NEW_EVENT,
  UPDATE_EVENT,
  SAVE_EVENT,
  POST_SAVE_FOLLOW_UP,
  EDIT_EVENT,
  CANCEL_EDIT_EVENT,
  SET_LOCAL_EVENT_FILES,
  DELETE_EVENT,
  DELETE_EVENT_ASSET,
}

interface IState {
  id?: string
  isDirty: boolean
  isEditing: boolean
  isSaving: boolean
  lease: ILease | undefined
  errors: LeaseFormErrors
  localFiles: IFile[]
  isCreatingEvent: boolean
  editingEventId: string
  editingEvent: ILeaseFullEvent
  isSavingEvent: boolean
  localEventFiles: IFile[]
  eventErrors: FormErrors<ILeaseEvent>
}
export interface ILeaseHook extends IState {
  update: (path: string, val: any, save?: boolean, notDirty?: boolean) => void
  setLocalFiles: (localFiles: IFile[]) => void
  setEditing: (isEditing: boolean) => void
  setErrors: (errors: LeaseFormErrors) => void
  setSaving: (isSaving: boolean) => void
  setLease: (lease: ILease) => void
  remove: () => Promise<any>
  reload: () => void
  addNewEvent: () => void
  updateEditEvent: (id: string) => void
  cancelEditEvent: () => void
  updateEvent: (category: string, value: any) => void
  setLocalEventFiles: (localFiles: IFile[]) => void
  deleteEvent: (id: string) => void
  saveEvent: (
    event: ILeaseFullEvent | ILeaseEvent,
    useEventProp: boolean,
    callback?: () => void
  ) => void
  deleteEventAsset: (event: ILeaseFullEvent, file: IAsset | IFile) => void
}

function reducer(state: IState, action: any) {
  switch (action.type) {
    case Actions.LOAD:
      return {
        ...state,
        isSaving: false,
        isDirty: false,
        lease: action.lease,
      }
    case Actions.UPDATE:
      const lease = state.lease ? { ...state.lease } : {}
      set(lease, action.path, action.val)
      return {
        ...state,
        isDirty: true,
        lease,
      }
    case Actions.SET_IS_SAVING:
      return {
        ...state,
        isSaving: action.isSaving,
      }
    case Actions.SET_IS_DIRTY:
      return {
        ...state,
        isDirty: action.isDirty,
      }
    case Actions.SET_LOCAL_FILES:
      return {
        ...state,
        isDirty: !!action.localFiles,
        localFiles: action.localFiles,
      }
    case Actions.SET_ERRORS:
      return {
        ...state,
        errors: action.errors,
        isSaving: false,
      }
    case Actions.SET_IS_EDITING:
      return {
        ...state,
        isEditing: action.isEditing,
      }
    case Actions.SET_LOCAL_EVENT_FILES:
      return {
        ...state,
        localEventFiles: action.localEventFiles,
      }
    case Actions.ADD_NEW_EVENT:
      if (!state.lease) {
        return state
      }

      const newEvent = cloneDeep(editingEventInit)
      newEvent.id = 'NEW'

      return {
        ...state,
        isCreatingEvent: true,
        editingEventId: 'NEW',
        eventErrors: {},
        editingEvent: newEvent,
        lease: {
          ...state.lease,
          contractEvents: [newEvent, ...state.lease.contractEvents],
        },
      }

    case Actions.EDIT_EVENT:
      // Editing can only be done when state.lease exists
      if (!state.lease) {
        return state
      }
      const index = state.lease.contractEvents.findIndex((e: ILeaseFullEvent) => e.id === action.id)
      const updatingObject = cloneDeep(state.lease.contractEvents[index])
      // @ts-ignore
      // -- updatedObject[action.category] = action.value
      return {
        ...state,
        isCreatingEvent: false,
        editingEventId: action.id,
        eventErrors: {},
        editingEvent: updatingObject,
        lease: {
          ...state.lease,
          contractEvents: state.lease.contractEvents.filter((e) => e.id !== 'NEW'),
        },
      }

    case Actions.UPDATE_EVENT:
      if (!state.lease) {
        return state
      }

      let newVal = action.value
      if (action.category === 'amount') {
        newVal = Number(newVal)
      }

      return {
        ...state,
        editingEvent: {
          ...state.editingEvent,
          [action.category]: newVal,
        },
      }
    case Actions.CANCEL_EDIT_EVENT:
      if (!state.lease) {
        return state
      }

      return {
        ...state,
        editingEvent: cloneDeep(editingEventInit),
        isCreatingEvent: false,
        editingEventId: '',
        localEventFiles: [],
        eventErrors: {},
        lease: {
          ...state.lease,
          contractEvents: state.lease.contractEvents.filter((e) => e.id !== 'NEW'),
        },
      }
    case Actions.DELETE_EVENT:
      if (!state.lease) {
        return state
      }
      return {
        ...state,
        lease: {
          ...state.lease,
          contractEvents: state.lease.contractEvents.filter((e) => e.id !== action.id),
        },
      }
    case Actions.SAVE_EVENT:
      if (!state.lease) {
        return state
      }

      return {
        ...state,
        isSavingEvent: true,
      }
    case Actions.POST_SAVE_FOLLOW_UP:
      if (!state.lease) {
        return state
      }

      if (action.errors) {
        return { ...state, eventErrors: action.errors, isSavingEvent: false }
      }

      let editedIndex = state.lease.contractEvents.findIndex((e) => e.id === action.event.id)
      const postSaveEvents = cloneDeep(state.lease.contractEvents)
      if (editedIndex === -1) {
        editedIndex = state.lease.contractEvents.findIndex((e) => e.id === 'NEW')
      }
      postSaveEvents[editedIndex] = action.event

      return {
        ...state,
        lease: {
          ...state.lease,
          contractEvents: postSaveEvents,
        },
        eventErrors: {},
        editingEvent: cloneDeep(editingEventInit),
        editingEventId: '',
        localEventFiles: [],
        isCreatingEvent: false,
        isSavingEvent: false,
      }
    case Actions.DELETE_EVENT_ASSET:
      if (!state.lease) {
        return state
      }

      const updatedEventIndex = state.lease.contractEvents.findIndex((e) => e.id === action.eventId)
      const updatedEvent = cloneDeep(state.lease.contractEvents[updatedEventIndex])
      const updatedEvents = cloneDeep(state.lease.contractEvents)
      const updatedEventAssets = updatedEvent.files.filter((a) => a.id !== action.assetId)
      updatedEvent.files = updatedEventAssets
      updatedEvents[updatedEventIndex] = updatedEvent

      return {
        ...state,
        lease: { ...state.lease, contractEvents: updatedEvents },
      }

    default:
      return state
  }
}

const editingEventInit = {
  id: '',
  amount: 0,
  createdAt: '',
  createdBy: { name: '' },
  date: null,
  description: '',
  eventType: 'Renewal',
  units: '$',
  files: [],
  dateAlert: false,
}

export default function useLease(id?: string): ILeaseHook {
  const [data, dispatch] = useReducer(reducer, {
    id: id === 'new' ? undefined : id,
    localFiles: [],
    localEventFiles: [],
    isCreatingEvent: false,
    editingEventId: '',
    editingEvent: cloneDeep(editingEventInit),
    isSavingEvent: false,
    isEditing: id === 'new' || !id,
    isDirty: false,
    isSaving: false,
    lease: undefined,
    errors: {},
    eventErrors: {},
  })

  const load = useCallback(() => {
    if (!data.id) return
    api.lease.details(data.id).then((lease) => dispatch({ type: Actions.LOAD, lease }))
  }, [data.id])
  useEffect(load, [data.id])

  return {
    ...data,
    update: async (path: string, val: any, save?: boolean, notDirty?: boolean) => {
      await dispatch({ type: Actions.UPDATE, path, val })
      if (notDirty) {
        await dispatch({ type: Actions.SET_IS_DIRTY, isDirty: false })
      }
      if (!save || !data.lease) return
      const _lease: ILease = { ...data.lease }
      set(_lease, path, val)
      api.lease.save(_lease).then(({ lease }) => {
        Toaster.success({ message: 'Changes saved' })
      })
    },
    remove: () => {
      if (!data.lease || !data.lease.id) return Promise.resolve()
      return api.lease.remove(data.lease.id)
    },
    reload: load,
    setLease: (lease: ILease) => dispatch({ type: Actions.LOAD, lease }),
    setErrors: (errors: LeaseFormErrors) => dispatch({ type: Actions.SET_ERRORS, errors }),
    setEditing: (isEditing: boolean) => dispatch({ type: Actions.SET_IS_EDITING, isEditing }),
    setSaving: (isSaving: boolean) => dispatch({ type: Actions.SET_IS_SAVING, isSaving }),
    setLocalFiles: (localFiles: IFile[]) => dispatch({ type: Actions.SET_LOCAL_FILES, localFiles }),
    addNewEvent: () => dispatch({ type: Actions.ADD_NEW_EVENT }),
    updateEditEvent: (id: string) => dispatch({ type: Actions.EDIT_EVENT, id }),
    updateEvent: (category: string, value: any) =>
      dispatch({
        type: Actions.UPDATE_EVENT,
        category,
        value,
      }),
    cancelEditEvent: () => dispatch({ type: Actions.CANCEL_EDIT_EVENT }),
    setLocalEventFiles: (localEventFiles: IFile[]) =>
      dispatch({ type: Actions.SET_LOCAL_EVENT_FILES, localEventFiles }),
    deleteEvent: (id: string) => {
      dispatch({ type: Actions.DELETE_EVENT, id })
      api.lease.event
        .deleteEvent(id)
        .then(() => Toaster.success({ message: 'Successfully deleted event' }))
        .catch((error: ApolloError) => Toaster.fail({ error }))
    },
    saveEvent: (
      event: ILeaseFullEvent | ILeaseEvent,
      useEventProp: boolean,
      callback?: () => void
    ) => {
      dispatch({ type: Actions.SAVE_EVENT })
      if (data.lease)
        if (event.id !== 'NEW') {
          api.lease.event
            .update(useEventProp ? event : data.editingEvent)
            .then((event: ILeaseFullEvent) => {
              if (data.localEventFiles.length) {
                api.lease.event
                  .addAssets(event.id, data.localEventFiles)
                  .then((uploadedFiles: IAsset[]) => {
                    const contractEventWithFiles = cloneDeep(event)
                    contractEventWithFiles.files = [
                      ...contractEventWithFiles.files,
                      ...uploadedFiles,
                    ]
                    dispatch({
                      type: Actions.POST_SAVE_FOLLOW_UP,
                      event: contractEventWithFiles,
                    })
                    Toaster.success({ message: 'Event Updated' })
                    callback?.()
                  })
              } else {
                dispatch({ type: Actions.POST_SAVE_FOLLOW_UP, event })
                Toaster.success({ message: 'Event Updated' })
                callback?.()
              }
            })
            .catch((error: ApolloError) => {
              // invalidArguments are part of our custom api error handling
              // but not standard everywhere
              dispatch({
                type: Actions.POST_SAVE_FOLLOW_UP,
                event,
                // @ts-ignore
                errors: error.graphQLErrors[0]?.invalidArguments,
              })
              Toaster.fail({ error })
            })
        } else {
          api.lease.event
            .create(data.lease.id, data.editingEvent)
            .then((event: ILeaseFullEvent) => {
              if (data.localEventFiles.length) {
                api.lease.event
                  .addAssets(event.id, data.localEventFiles)
                  .then((uploadedFiles: IAsset[]) => {
                    const newContractEvent = cloneDeep(event)
                    newContractEvent.files = uploadedFiles
                    dispatch({
                      type: Actions.POST_SAVE_FOLLOW_UP,
                      event: newContractEvent,
                    })
                    Toaster.success({ message: 'Event Saved' })
                  })
              } else {
                dispatch({ type: Actions.POST_SAVE_FOLLOW_UP, event })
                Toaster.success({ message: 'Event Saved' })
              }
            })
            .catch((error: ApolloError) => {
              // invalidArguments are part of our custom api error handling
              // but not standard everywhere
              dispatch({
                type: Actions.POST_SAVE_FOLLOW_UP,
                event,
                // @ts-ignore
                errors: error.graphQLErrors[0]?.invalidArguments,
              })
              Toaster.fail({ error })
            })
        }
    },
    deleteEventAsset: (event: ILeaseFullEvent, file: IAsset | IFile) => {
      dispatch({
        type: Actions.SET_LOCAL_EVENT_FILES,
        localEventFiles: data.localEventFiles.filter((f: IFile) => f !== file),
      })
      const _files = get(event, 'files', [])
      dispatch({
        type: Actions.UPDATE_EVENT,
        category: 'files',
        value: _files.filter((f) => f !== file),
      })
      if ('id' in file)
        api.lease.event.deleteAsset(file.id).then((id: string) =>
          dispatch({
            type: Actions.DELETE_EVENT_ASSET,
            eventId: event.id,
            assetId: id,
          })
        )
    },
  }
}
